import React, {useEffect, useState} from 'react';
import {CButton, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CTextarea} from '@coreui/react';
import {getApi} from "../../apis/backendApis";
import useAlert from "../../services/useAlert";
import {LinhaVersaoFluxo, PreviaVersaoFluxo, Processo, dataBr, erroDaApi} from "./modelo";

interface Props {
    processo: Processo,
    onFechar: () => void,
    onAtualizado: (processo: Processo) => void,
}

const VERBO: {[acao: string]: string} = {incluido: 'Inclui', alterado: 'Altera', removido: 'Remove'};
const ESCOPO: {[escopo: string]: string} = {item: 'o item', etapa: 'a etapa', papel: 'o papel'};

const periodo = (valor: string | null) => (valor ? valor.split('/').map(dataBr).join(' a ') : 'nova');

const Linha = ({linha, mantido}: {linha: LinhaVersaoFluxo, mantido?: boolean}) => (
    <li>
        <span>
            {mantido && linha.acao === 'removido' ? 'Saiu do fluxo:' : mantido ? 'Mudou no fluxo:' : VERBO[linha.acao]} {ESCOPO[linha.escopo]} <strong>{linha.nome}</strong>
            {linha.escopo === 'item' && linha.etapa ? <> na etapa {linha.etapa}</> : null}
        </span>
        {linha.campos.length > 0 && (
            <ul className="pr-versao-campos">
                {linha.campos.map((c, i) => (
                    <li key={i}>{c.campo}: <s>{c.de ?? '(vazio)'}</s> → {c.para ?? '(vazio)'}</li>
                ))}
            </ul>
        )}
        {linha.motivo && <em>{linha.motivo}</em>}
    </li>
);

/**
 * Atualizar o processo para a versao atual do fluxo (#485540): mostra a previa
 * (o que muda, o que mexe no que ja foi executado e o que fica como esta) e
 * aplica. Com impacto, so aplica com o "estou ciente" marcado.
 */
const VersaoFluxoModal = ({processo, onFechar, onAtualizado}: Props) => {
    const [showAlert] = useAlert();
    const [previa, setPrevia] = useState<PreviaVersaoFluxo | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [motivo, setMotivo] = useState('');
    const [ciente, setCiente] = useState(false);
    const [processando, setProcessando] = useState(false);

    const carregar = () => {
        setPrevia(null);
        setErro(null);
        setCiente(false);
        getApi().get(`processo/versao-fluxo/${processo.id}`)
            .then(r => setPrevia(r.data as PreviaVersaoFluxo))
            .catch(e => setErro(erroDaApi(e, 'Não foi possível calcular a atualização.')));
    };

    useEffect(carregar, [processo.id]);

    const aplicar = () => {
        if (!previa) {
            return;
        }
        const corpo = new FormData();
        corpo.append('data', JSON.stringify({versao: previa.versao_fluxo, confirma_impactos: ciente, motivo}));
        setProcessando(true);
        setErro(null);
        getApi().post(`processo/versao-fluxo/${processo.id}`, corpo)
            .then(resposta => {
                showAlert({msg: resposta.data.msg ?? 'Processo atualizado.', type: 'success', toaster: true});
                onAtualizado(resposta.data.processo as Processo);
            })
            .catch(e => setErro(erroDaApi(e, 'Erro ao atualizar o processo.')))
            .finally(() => setProcessando(false));
    };

    const impactos = previa ? previa.confirmar.length : 0;
    const nadaMuda = previa !== null && !previa.aplicar.length && !impactos;

    return (
        <CModal show onClose={onFechar} centered size="lg">
            <CModalHeader closeButton>
                <CModalTitle>Atualizar para a versão {previa?.versao_fluxo ?? processo.fluxo.versao_atual} do fluxo</CModalTitle>
            </CModalHeader>
            <CModalBody className="pr-versao">
                {!previa && !erro && <p className="pr-ajuda">Comparando o processo com o fluxo...</p>}
                {previa?.em_dia && <p className="pr-ajuda">O processo já está na versão atual do fluxo.</p>}
                {previa && !previa.em_dia && (
                    <>
                        <p className="pr-ajuda">
                            O processo foi gerado da versão {previa.versao_processo} do fluxo {processo.fluxo.codigo}; o fluxo está na versão {previa.versao_fluxo}.{' '}
                            {previa.refaz_meta
                                ? 'Como nada foi executado ainda, o cronograma é refeito a partir do início, com a meta original junto.'
                                : 'O que já foi respondido não muda, e o histórico fica. Só a previsão do cronograma muda: a meta original continua para mostrar o desvio.'}
                        </p>

                        {nadaMuda && <p className="pr-ajuda">Nada muda neste processo: a atualização só registra a versão nova.</p>}

                        {previa.aplicar.length > 0 && (
                            <section>
                                <h6>Será atualizado ({previa.aplicar.length})</h6>
                                <ul className="pr-versao-lista">
                                    {previa.aplicar.map((linha, i) => <Linha key={i} linha={linha}/>)}
                                </ul>
                            </section>
                        )}

                        {impactos > 0 && (
                            <section className="pr-versao-impactos">
                                <h6>Mexe no que já foi executado ({impactos})</h6>
                                <ul className="pr-versao-lista">
                                    {previa.confirmar.map((linha, i) => <Linha key={i} linha={linha}/>)}
                                </ul>
                            </section>
                        )}

                        {previa.mantidos.length > 0 && (
                            <section className="pr-versao-mantidos">
                                <h6>Fica como está ({previa.mantidos.length})</h6>
                                <ul className="pr-versao-lista">
                                    {previa.mantidos.map((linha, i) => <Linha key={i} linha={linha} mantido/>)}
                                </ul>
                            </section>
                        )}

                        {previa.papeis_sem_pessoa.length > 0 && (
                            <div className="pr-aviso">
                                Papel novo sem pessoa: {previa.papeis_sem_pessoa.map(p => `${p.nome} (${p.codigo})`).join(', ')}.
                                Depois de atualizar, escolha quem ocupa na aba Responsáveis do processo.
                            </div>
                        )}

                        <section>
                            <h6>Cronograma</h6>
                            <p className="pr-ajuda">
                                Término previsto: {previa.fim_atual === previa.fim_novo
                                    ? <>continua em {dataBr(previa.fim_novo)}</>
                                    : <><s>{dataBr(previa.fim_atual)}</s> → <strong>{dataBr(previa.fim_novo)}</strong></>}
                            </p>
                            {previa.cronograma.length > 0 && (
                                <ul className="pr-versao-campos">
                                    {previa.cronograma.map((c, i) => (
                                        <li key={i}>{c.etapa}: {c.de ? <s>{periodo(c.de)}</s> : 'etapa nova'} → {periodo(c.para)}</li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        <label className="pr-versao-motivo">
                            <span>Motivo (opcional, vai para o histórico)</span>
                            <CTextarea rows={2} value={motivo} onChange={(e: any) => setMotivo(e.target.value)}/>
                        </label>

                        {impactos > 0 && (
                            <label className="pr-versao-ciente">
                                <input type="checkbox" checked={ciente} onChange={e => setCiente(e.target.checked)}/>
                                Vi o que mexe no que já foi executado e quero atualizar
                            </label>
                        )}
                    </>
                )}
                {/* Perto dos botoes: quem clica em "Atualizar processo" esta no fim do modal. */}
                {erro && (
                    <div className="pr-aviso pr-versao-erro" role="alert">
                        {erro.split('\n').map((linha, i) => <div key={i}>{linha}</div>)}
                        {/mudou depois da prévia/.test(erro) && (
                            <CButton size="sm" color="link" className="p-0" onClick={carregar}>Ver a prévia de novo</CButton>
                        )}
                        {/acabou de ser atualizado/.test(erro) && (
                            <CButton size="sm" color="link" className="p-0" onClick={() => window.location.reload()}>Recarregar a página</CButton>
                        )}
                    </div>
                )}
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" variant="outline" onClick={onFechar}>Cancelar</CButton>
                <CButton color="primary" disabled={!previa || previa.em_dia || processando || (impactos > 0 && !ciente)} onClick={aplicar}>
                    {processando ? 'Atualizando...' : 'Atualizar processo'}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

export default VersaoFluxoModal;
